import React from "react";
import { fmtNum, sourceLabel } from "./trafficUtils";

export function TrafficDelta({ pct, title, uneven }) {
	if (pct == null || pct === 0) return null;
	const up = pct > 0;
	return (
		<span
			title={title}
			className={`monitoring__ai-traffic-delta ${
				up ? "monitoring__ai-traffic-delta--up" : "monitoring__ai-traffic-delta--down"
			}`}
		>
			{up ? "▲" : "▼"} {Math.abs(Number(pct)).toFixed(uneven ? 1 : 0)}%
		</span>
	);
}

export function TrafficSourceBadge({ source }) {
	if (!source) return null;
	const key = String(source).toLowerCase();
	const tone =
		key === "cloudflare"
			? "monitoring__ai-traffic-source-badge--cf"
			: key === "gsc"
				? "monitoring__ai-traffic-source-badge--gsc"
				: key === "bing"
					? "monitoring__ai-traffic-source-badge--bing"
					: key === "ga4"
						? "monitoring__ai-traffic-source-badge--ga4"
						: "";
	return (
		<span className={`monitoring__ai-traffic-source-badge ${tone}`}>
			{sourceLabel(source)}
		</span>
	);
}

function TrafficVariance({ kpi }) {
	if (!kpi?.variance || !kpi.source) return null;
	const entries = Object.entries(kpi.variance);
	if (!entries.length) return null;
	return (
		<div
			className="monitoring__ai-traffic-variance"
			title="A second source measured the same metric. The gap is measurement loss — the headline stays single-sourced."
		>
			{sourceLabel(kpi.source)} {fmtNum(kpi.value)} ·{" "}
			{entries.map(([s, v]) => `${sourceLabel(s)} ${fmtNum(v)}`).join(" · ")}
		</div>
	);
}

export function TrafficCard({ label, children, hint }) {
	return (
		<div className="monitoring__ai-traffic-kpi-card">
			<div className="monitoring__ai-traffic-kpi-label">{label}</div>
			{children}
			{hint && <div className="monitoring__ai-traffic-kpi-hint">{hint}</div>}
		</div>
	);
}

function formatKpiValue(value, decimals) {
	if (value == null || Number.isNaN(Number(value))) return "—";
	if (decimals != null) return Number(value).toFixed(decimals);
	return fmtNum(value);
}

export function TrafficKpiCard({
	label,
	kpi,
	connected,
	connectHint,
	unit,
	hint,
	danger,
	compareLabel: compareLbl,
	uneven,
	valueDecimals,
}) {
	const hasData = kpi?.value != null;
	const show = connected || hasData;
	const pct = uneven
		? kpi?.per_day_delta_pct ?? kpi?.delta_pct ?? null
		: kpi?.delta_pct ?? null;

	return (
		<TrafficCard label={label} hint={show ? hint : undefined}>
			{show ? (
				<>
					<div className="monitoring__ai-traffic-kpi-value-row">
						<span
							className={`monitoring__ai-traffic-kpi-value${
								danger && (kpi.value || 0) > 0
									? " monitoring__ai-traffic-kpi-value--danger"
									: ""
							}`}
						>
							{formatKpiValue(kpi.value, valueDecimals)}
							{unit && (
								<span className="monitoring__ai-traffic-kpi-unit">{unit}</span>
							)}
						</span>
						<TrafficSourceBadge source={kpi.source} />
						<TrafficDelta
							pct={pct}
							uneven={uneven}
							title={
								uneven
									? `Per-day rate ${compareLbl ?? ""} — windows cover different numbers of days`
									: compareLbl
							}
						/>
					</div>
					{kpi.per_day != null && (
						<div className="monitoring__ai-traffic-per-day">
							{kpi.per_day}/day
							{kpi.per_day_previous != null && ` vs ${kpi.per_day_previous}/day`}
						</div>
					)}
					{kpi.urls_affected ? (
						<div className="monitoring__ai-traffic-urls-affected">
							{kpi.urls_affected} URL{kpi.urls_affected === 1 ? "" : "s"} affected
							{kpi.attributed_requests != null &&
							kpi.value != null &&
							kpi.attributed_requests < kpi.value ? (
								<span className="monitoring__ai-traffic-attributed">
									{` · ${kpi.attributed_requests} of ${kpi.value} attributed`}
								</span>
							) : null}
						</div>
					) : null}
					<TrafficVariance kpi={kpi} />
				</>
			) : (
				<div className="monitoring__ai-traffic-connect-prompt">
					Connect <strong>{connectHint}</strong> to measure this.
				</div>
			)}
		</TrafficCard>
	);
}

export function TrafficHeadlineCard({
	label,
	value,
	badge,
	badgeTone,
	kpi,
	uneven,
	compareLabel: compareLbl,
}) {
	const pct = uneven ? kpi?.per_day_delta_pct ?? null : kpi?.delta_pct ?? null;
	return (
		<div className="monitoring__ai-traffic-kpi-card monitoring__ai-traffic-headline-card">
			<div className="monitoring__ai-traffic-kpi-label">{label}</div>
			<div className="monitoring__ai-traffic-kpi-value-row">
				<span className="monitoring__ai-traffic-headline-value">{value}</span>
				<span className={`monitoring__ai-traffic-source-badge ${badgeTone}`}>
					{badge}
				</span>
				<TrafficDelta
					pct={pct}
					uneven={uneven}
					title={
						uneven
							? `Per-day rate ${compareLbl ?? ""} — windows cover different numbers of days`
							: compareLbl
					}
				/>
			</div>
			{kpi?.per_day != null && (
				<div className="monitoring__ai-traffic-per-day">
					{kpi.per_day}/day
					{kpi.per_day_previous != null && ` vs ${kpi.per_day_previous}/day`}
				</div>
			)}
		</div>
	);
}

export function TrafficTableTh({ children, className = "" }) {
	return (
		<th scope="col" className={`monitoring__ai-traffic-th ${className}`.trim()}>
			{children}
		</th>
	);
}
